import Model from 'src/app/common/model';
import CustomerDeviceModel from './customerDevice.model';
import DeviceModelModel from './deviceModel.model';

/**
 * @model Device
 * @description
 * Represents a physical hardware unit (asset) within the AquaCloud ecosystem.
 * This model serves as the master record for a specific piece of equipment, identified by its
 * serial number, before or independent of its assignment to a specific customer.
 *
 * **Domain Context:**
 * In the device lifecycle (Manufacturing -> Inventory -> Provisioning -> Deployment),
 * this model captures the "Manufacturing" and "Provisioning" states. It holds the
 * immutable identity of the hardware (`serial_number`) and its factory configuration.
 *
 * **Purpose:**
 * To manage the inventory of devices and their technical configurations.
 * It answers: "What is this device?", "When was it made?", "What are its default credentials?".
 *
 * **Key Responsibilities:**
 * 1. **Asset Identity**: Stores the unique `serial_number` and `device_model_id`.
 * 2. **Configuration**: Holds network credentials (`ssid`, `password`) and connection details (`port`, `url`) used during device setup.
 * 3. **History**: Tracks the device's assignment history via `CustomerDeviceData`.
 * 4. **Firmware/Data**: Manages file associations (`fileName`, `filePath`) potentially for firmware updates or calibration data.
 *
 * **Data Flow:**
 * - **Ingestion**: Created when new devices are manufactured or imported into the system.
 * - **Provisioning**: Used by the mobile app or desktop tool to configure the physical device (e.g., setting Wi-Fi).
 * - **Assignment**: Linked to a `CustomerDeviceModel` when assigned to a user.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can create and manage device inventory.
 * - **Technician**: Uses this data to provision devices in the field.
 *
 * **Features:**
 * - Supports specific hardware types like Calorimeters (`isCalorimeter`).
 * - Handles file uploads/downloads related to the device.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class Device extends Model {
  /**
   * @type {Array<CustomerDeviceModel>}
   * @description History of customer assignments for this device.
   * Allows tracking who has owned or used this specific unit over time.
   * @optional
   */
  CustomerDeviceData?: Array<CustomerDeviceModel>;

  /**
   * @type {DeviceModelModel}
   * @description The classification or model definition of the device (e.g., "Pro Sensor v2").
   * Populated via join operations.
   * @optional
   */
  deviceModel?: DeviceModelModel;

  /**
   * @type {string}
   * @description The unique hardware serial number.
   * Critical for identification and warranty tracking.
   * @optional
   */
  serial_number?: string;

  /**
   * @type {number}
   * @description Foreign key referencing the `DeviceModel` table.
   * @optional
   */
  device_model_id?: number;

  /**
   * @type {string}
   * @description Alternative identifier or asset tag number.
   * @optional
   */
  number?: string;

  /**
   * @type {string}
   * @description The date the device was manufactured.
   * Format: ISO 8601 date string.
   * @optional
   */
  manufactured_date?: string;

  /**
   * @type {string}
   * @description Timestamp of the last update to this record.
   * @optional
   */
  updated_date?: string;

  /**
   * @type {string}
   * @description Timestamp when the device record was created in the system.
   * @optional
   */
  created_date?: string;

  /**
   * @type {boolean}
   * @description Flag indicating if this device is a Calorimeter.
   * Used to trigger specific UI logic or data processing for feed analysis.
   * @optional
   */
  isCalorimeter?: boolean;

  /**
   * @type {string}
   * @description Name of an associated file (e.g., firmware binary, config file).
   * @optional
   */
  fileName?: string;

  /**
   * @type {string}
   * @description Storage path or URL for the associated file.
   * @optional
   */
  filePath?: string;

  /**
   * @type {string}
   * @description Base64 encoded content or raw data of the file.
   * @optional
   */
  fileData?: string;

  /**
   * @type {string}
   * @description Communication port identifier (e.g., "COM1", "/dev/ttyUSB0") for direct connection.
   * @optional
   */
  port?: string;

  /**
   * @type {string}
   * @description Authentication key or token for the device.
   * @optional
   */
  key?: string;

  /**
   * @type {string}
   * @description Default or configured Wi-Fi SSID for the device.
   * @optional
   */
  ssid?: string;

  /**
   * @type {string}
   * @description Default or configured Wi-Fi password.
   * @optional
   */
  password?: string;

  /**
   * @type {string}
   * @description API endpoint URL the device is configured to communicate with.
   * @optional
   */
  url?: string;

  /**
   * @type {string}
   * @description Status string indicating upload progress or state.
   * @optional
   */
  uploaded?: string;

  /**
   * @type {string}
   * @description Generic data payload field.
   * @optional
   */
  data?: string;

  /**
   * @type {boolean}
   * @description Flag indicating if a download operation is pending or active.
   * @optional
   */
  download?: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating availability or status of the I/O port.
   * @optional
   */
  ioPort?: boolean;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the Device model.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}
